<template>
  <div v-if="group">
    <base-page-title :titleText="pageTitle" />

    <b-col class="col-12 col-md-8 p-3 mx-auto">
      <!-- Group Info Card -->
      <b-card class="mb-4">
        <template #header>
          <div class="d-flex align-items-center justify-content-between">
            <span class="font-weight-bold">{{ $t("page.groups.groupInfo") }}</span>
            <div>
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-2"
                @click="showEditModal = true"
              >
                {{ $t("button.edit") }}
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="confirmDelete">
                {{ $t("button.delete") }}
              </b-button>
            </div>
          </div>
        </template>

        <b-row class="mb-2">
          <b-col cols="4" class="text-muted">{{ $t("page.groups.groupName") }}</b-col>
          <b-col>{{ group.name || "—" }}</b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col cols="4" class="text-muted">{{ $t("page.groups.licenses") }}</b-col>
          <b-col>{{ group.licenses }}</b-col>
        </b-row>
        <b-row v-if="group.allowedDomains && group.allowedDomains.length > 0" class="mb-2">
          <b-col cols="4" class="text-muted">{{ $t("page.groups.allowedDomains") }}</b-col>
          <b-col>
            <b-badge
              v-for="d in group.allowedDomains"
              :key="d.domain"
              :variant="d.status === 'active' ? 'success' : 'secondary'"
              class="mr-1"
            >
              {{ d.domain }}
            </b-badge>
          </b-col>
        </b-row>
      </b-card>

      <!-- Members Card -->
      <b-card>
        <group-members
          :group-id="group._id"
          :admin-id="group.admin || group.adminId"
          :members="group.members || []"
        />
      </b-card>
    </b-col>

    <!-- Edit Modal -->
    <b-modal
      :visible="showEditModal"
      :title="$t('page.groups.editGroup')"
      @hide="showEditModal = false"
      @ok.prevent="submitEdit"
      :ok-title="$t('button.saveChanges')"
      :cancel-title="$t('button.cancel')"
    >
      <b-form @submit.prevent="submitEdit">
        <b-form-group :label="$t('page.groups.groupName')" label-for="edit-name">
          <b-form-input id="edit-name" v-model="editForm.name" />
        </b-form-group>
        <b-form-group :label="$t('page.groups.licenses')" label-for="edit-licenses">
          <b-form-input
            id="edit-licenses"
            v-model.number="editForm.licenses"
            type="number"
            min="1"
            required
          />
        </b-form-group>
        <b-form-group :label="$t('page.groups.allowedDomains')">
          <div
            v-for="(entry, index) in editForm.allowedDomains"
            :key="index"
            class="d-flex mb-1"
          >
            <b-form-input
              v-model="entry.domain"
              :placeholder="$t('page.groups.domainPlaceholder')"
              class="mr-2"
            />
            <b-form-select
              v-model="entry.status"
              :options="domainStatusOptions"
              style="max-width: 110px"
              class="mr-2"
            />
            <b-button
              variant="outline-danger"
              size="sm"
              @click="editForm.allowedDomains.splice(index, 1)"
            >
              &times;
            </b-button>
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="editForm.allowedDomains.push({ domain: '', status: 'active' })"
          >
            + {{ $t("page.groups.addDomain") }}
          </b-button>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import GroupMembers from "../components/Groups/GroupMembers.vue";

export default {
  components: { GroupMembers },
  data() {
    return {
      pageTitle: "page.groups.groupDetails",
      showEditModal: false,
      editForm: { name: "", licenses: 1, allowedDomains: [] },
      domainStatusOptions: [
        { value: "active", text: "Active" },
        { value: "inactive", text: "Inactive" },
      ],
    };
  },
  computed: {
    group() {
      return this.$store.getters["groups/getActiveGroup"];
    },
  },
  watch: {
    showEditModal(val) {
      if (val && this.group) {
        this.editForm = {
          name: this.group.name || "",
          licenses: this.group.licenses,
          allowedDomains: (this.group.allowedDomains || []).map(d => ({ ...d })),
        };
      }
    },
  },
  methods: {
    confirmDelete() {
      this.$store.commit("confirm/SET_CONFIRM", {
        titleText: "page.groups.deleteGroupTitle",
        messageText: "page.groups.deleteGroupMessage",
        actionBtnLabel: "button.delete",
        actionBtnCallback: () => this.deleteGroup(),
      });
    },
    deleteGroup() {
      this.$store.commit("loader/SET_LOADER", {});
      this.$store
        .dispatch("groups/deleteGroup", this.group._id)
        .then(() => this.$router.push({ name: "Groups" }))
        .catch(() => {})
        .finally(() => this.$store.dispatch("loader/close"));
    },
    submitEdit() {
      const payload = { licenses: this.editForm.licenses };
      if (this.editForm.name) payload.name = this.editForm.name;
      if (this.editForm.allowedDomains.length > 0) {
        payload.allowedDomains = this.editForm.allowedDomains.filter(d => d.domain);
      }
      this.$store.commit("loader/SET_LOADER", {});
      this.$store
        .dispatch("groups/updateGroup", { groupId: this.group._id, payload })
        .then(() => {
          this.showEditModal = false;
        })
        .catch(() => {})
        .finally(() => this.$store.dispatch("loader/close"));
    },
  },
};
</script>

<style scoped></style>
